/**
 * Code 128 as SVG, for the patient wristband — PHASE-0.md §2.6,
 * ARCHITECTURE.md §11 ("streckkoder som SVG i klienten").
 *
 * **What a wrong barcode costs.** A wristband is scanned to answer *is this the
 * patient I think it is*. A barcode that decodes to a different identifier than
 * the one printed beside it is a patient-misidentification mechanism with a
 * machine's authority behind it, and the clinician has no way to notice: the
 * scanner is trusted precisely because reading twelve digits off a wrist is
 * error-prone. So this module fails loudly rather than emitting anything it
 * cannot encode exactly, the same posture as ./../format/clinicalDose.ts, and
 * the wristband always prints the identifier as text as well — see
 * ./Wristband.tsx. If the scan and the text disagree, the text is the record.
 *
 * **Written here rather than taken as a dependency.** The original phase-0 plan
 * listed `jsbarcode`; this replaces it. Code 128 is a small closed symbology —
 * a 107-row table, one modulo-103 checksum, one stop pattern — and a dependency
 * would still have needed its own test vectors for the reason above, so
 * importing one bought nothing it did not also cost in `runtime_deps`
 * classification (AGENTS.md §8, DECISIONS.md #30, #33). The table below is held
 * by structural invariants in ./code128.test.ts, not by review: a mistyped row
 * is caught by arithmetic rather than by a careful reader.
 *
 * **No FNC1, no GS1 Application Identifiers, no Code Set A.** This encodes a
 * local identifier for a local scanner during an outage. GS1 structure would be
 * a claim about a national or supply-chain namespace that this system is not
 * part of.
 */

/**
 * Thrown rather than emitting a barcode that might scan as something else.
 *
 * There is no degraded rendering available. A blank where a barcode belongs is
 * a wristband somebody reprints; a barcode that encodes the wrong digits is a
 * wristband nobody questions.
 */
export class InvalidBarcodeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidBarcodeError'
  }
}

/**
 * The 107 Code 128 symbol patterns as element widths, left to right, starting
 * with a bar: bar, space, bar, space, bar, space.
 *
 * Index is the symbol value. 0–102 are the data and control characters, 103–105
 * are START A, START B and START C, and 106 is STOP — the only pattern with
 * seven elements and thirteen modules rather than six and eleven.
 *
 * Two invariants make this table self-checking, and ./code128.test.ts asserts
 * both over every row:
 *
 * - each of 0–105 sums to 11 modules, and STOP to 13;
 * - the three bars of each row sum to an **even** number of modules. That is
 *   Code 128's parity property, and it is what lets a scanner reject a
 *   misread symbol rather than returning a wrong character. A typo in this
 *   table almost always breaks it.
 */
const PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131',
  '211412', // 103 START A
  '211214', // 104 START B
  '211232', // 105 START C
  '2331112', // 106 STOP
]

/** START B — ASCII 32–126, one symbol per character. */
const START_B = 104

/** START C — digit pairs, one symbol per two digits. */
const START_C = 105

const STOP = 106

/**
 * Quiet zone either side, in modules.
 *
 * Ten is the specification's minimum. It is not decoration: a scanner needs
 * white space to recognise where the symbol begins, and a barcode butted
 * against a table rule or a fold in the wristband is one that reads short or
 * not at all. ./Wristband.tsx includes it inside the SVG viewBox rather than
 * relying on page margins, so a layout change cannot take it away.
 */
export const QUIET_ZONE_MODULES = 10

/** One dark bar, positioned in modules from the start of the quiet zone. */
export interface Code128Bar {
  /** Left edge, in modules. */
  readonly x: number
  /** Width, in modules. */
  readonly width: number
}

/** An encoded symbol, ready to render. */
export interface Code128Symbology {
  /**
   * The symbol values in order: start, data, check, stop.
   *
   * Exposed because this is the layer published test vectors describe — a
   * check digit is quoted as a number, not as a bar pattern — so
   * ./code128.test.ts can assert against them directly.
   */
  readonly symbols: readonly number[]

  /**
   * The modulo-103 check symbol. Already present in {@link symbols}; named
   * separately because it is the single most error-prone value in the
   * symbology and the tests say so out loud.
   */
  readonly checkSymbol: number

  /** Which character set encoded the payload, for the tests and for display. */
  readonly codeSet: 'B' | 'C'

  /** Total width including both quiet zones, in modules. */
  readonly modules: number

  /** The dark bars, left to right. */
  readonly bars: readonly Code128Bar[]
}

/** Every character Code Set C can encode as a pair, and nothing else. */
function isAllDigits(value: string): boolean {
  return /^[0-9]+$/.test(value)
}

/**
 * The data symbols for a payload, and the set that produced them.
 *
 * **Code Set C only for an all-digit payload of even length, and never
 * mid-string.** C packs two digits into one symbol, which is a 40 % narrower
 * symbol for a twelve-digit personnummer — the identifier 28 of the demo
 * ward's 30 patients carry, since `electronichealth.se/identifier/personnummer`
 * is twelve digits with no hyphen. Switching between sets *within* a payload
 * would save three more symbols on a `TST00000001` reservnummer and is where
 * every Code 128 implementation bug lives: the switch symbol, the parity of
 * the remaining run, and the checksum index all have to agree. Three symbols
 * on two patients does not buy that risk, so an odd-length or mixed payload is
 * encoded wholly in B. Both fit a wristband with room to spare.
 */
function dataSymbols(value: string): { symbols: number[]; codeSet: 'B' | 'C' } {
  if (isAllDigits(value) && value.length % 2 === 0) {
    const symbols: number[] = []
    for (let i = 0; i < value.length; i += 2) {
      symbols.push(Number.parseInt(value.slice(i, i + 2), 10))
    }
    return { symbols, codeSet: 'C' }
  }

  const symbols: number[] = []
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0

    // Code Set B is exactly ASCII 32–127, value = code − 32. 127 is DEL and is
    // refused with the rest of the controls: it is encodable and meaningless,
    // and an identifier containing one is a data defect, not something to
    // faithfully reproduce on a wristband.
    if (code < 32 || code > 126) {
      throw new InvalidBarcodeError(
        `Cannot encode ${JSON.stringify(character)} (U+${code.toString(16).toUpperCase().padStart(4, '0')}) ` +
          `in Code 128: only printable ASCII is supported. ` +
          `Swedish letters are deliberately not encodable here — an identifier never contains one ` +
          `(AGENTS.md §7), and a barcode is not the place to discover that something else was passed.`,
      )
    }

    symbols.push(code - 32)
  }

  return { symbols, codeSet: 'B' }
}

/**
 * Encode `value` as Code 128, ready to render as SVG.
 *
 * Intended for a patient identifier — a personnummer, samordningsnummer or
 * reservnummer, exactly as `clinical.patient_identifier` holds it. Pass the
 * recorded value unchanged: stripping a character to make it encode would mean
 * the barcode and the record disagree.
 *
 * Throws {@link InvalidBarcodeError} for an empty payload or any character
 * outside printable ASCII.
 */
export function encodeCode128(value: string): Code128Symbology {
  if (value === '') {
    throw new InvalidBarcodeError(
      'Cannot encode an empty value. A wristband with no identifier on it is not a wristband, ' +
        'and an unidentified patient is given a reservnummer precisely so that there is one.',
    )
  }

  const { symbols: data, codeSet } = dataSymbols(value)
  const start = codeSet === 'C' ? START_C : START_B

  // Checksum: the start value, plus each data symbol weighted by its
  // one-based position, modulo 103. The weighting is what makes it catch a
  // transposition — two swapped digits change the sum — which is the failure
  // an unweighted sum would wave through, and transposed digits in a
  // personnummer is the realistic way this goes wrong.
  let weighted = start
  data.forEach((symbol, index) => {
    weighted += symbol * (index + 1)
  })
  const checkSymbol = weighted % 103

  const symbols = [start, ...data, checkSymbol, STOP]

  const bars: Code128Bar[] = []
  let x = QUIET_ZONE_MODULES

  for (const symbol of symbols) {
    const pattern = PATTERNS[symbol]
    if (pattern === undefined) {
      // Unreachable: every path above produces 0–106. Stated rather than
      // assumed, because the alternative to a throw here is a silently
      // shortened symbol that scans as a different value.
      throw new InvalidBarcodeError(`No Code 128 pattern for symbol value ${symbol}.`)
    }

    // Elements alternate bar, space, bar, space… starting with a bar, so the
    // even positions are the dark ones.
    let position = 0
    for (const digit of pattern) {
      const width = Number.parseInt(digit, 10)
      if (position % 2 === 0) {
        bars.push({ x, width })
      }
      x += width
      position += 1
    }
  }

  return {
    symbols,
    checkSymbol,
    codeSet,
    modules: x + QUIET_ZONE_MODULES,
    bars,
  }
}

/**
 * The bars as a single SVG path `d`, in a user space where one unit is one
 * module and the bars run the full height.
 *
 * One path rather than one `<rect>` per bar: a twelve-digit personnummer is 50
 * bars, and a print stylesheet that has to lay out 50 elements per wristband is
 * slower to render and easier for a browser to hairline-round inconsistently.
 * A single path is one fill.
 *
 * @param symbology The result of {@link encodeCode128}.
 * @param height Bar height in the same module units as the widths.
 */
export function code128PathData(symbology: Code128Symbology, height: number): string {
  return symbology.bars.map((bar) => `M${bar.x} 0h${bar.width}v${height}h-${bar.width}z`).join('')
}
